import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Center, Container, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { Link } from 'react-router-dom';
import { buildSessionScopeKey, exchangeSSOTokens, saveSessionExpiry } from '../api/auth';
import { ApiError } from '../api/http';
import PageShell from '../components/PageShell';
import { useProgramConfig } from '../hooks/useProgramConfig';
import { clearReturnTo, getReturnTo } from '../hooks/returnTo';
import { hasUsableSSOConfig } from '../utils/authConfig';
import { buildPortalDestination, getPortalPathInfo } from '../utils/portalPath';
import { loadAndClearPKCEState } from '../utils/pkce';

export default function AuthSSOCallbackPage() {
  const { data: config, isLoading, error: configError } = useProgramConfig();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const exchangeAttempted = useRef(false);

  useEffect(() => {
    if (!config || exchangeAttempted.current) return;
    exchangeAttempted.current = true;

    if (!hasUsableSSOConfig(config.sso)) {
      setErrorMessage('SSO is not configured correctly for this program.');
      setStatus('error');
      return;
    }

    const ssoConfig = config.sso;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) {
      setErrorMessage('Missing authorization code or state parameter.');
      setStatus('error');
      return;
    }

    const { state: storedState, codeVerifier } = loadAndClearPKCEState();

    if (!storedState || storedState !== state) {
      setErrorMessage('State mismatch. Please try signing in again.');
      setStatus('error');
      return;
    }

    if (!codeVerifier) {
      setErrorMessage('Missing code verifier. Please try signing in again.');
      setStatus('error');
      return;
    }

    const { basename } = getPortalPathInfo();
    const redirectUri = `${window.location.origin}${basename}/auth/sso/callback`;

    void (async () => {
      try {
        const tokenEndpoint = ssoConfig.tokenUrl;
        const tokenRes = await fetch(tokenEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: ssoConfig.clientId,
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
          }),
        });

        if (!tokenRes.ok) {
          const errorData = await tokenRes.json().catch(() => ({})) as { error_description?: string; error?: string };
          throw new Error(errorData.error_description ?? errorData.error ?? `Token exchange failed (HTTP ${tokenRes.status})`);
        }

        const tokens = await tokenRes.json() as { access_token?: string; id_token?: string };
        if (!tokens.access_token || !tokens.id_token) {
          throw new Error('Token response is missing required tokens.');
        }

        const result = await exchangeSSOTokens(tokens.id_token, tokens.access_token);

        if (result.pendingLogin) {
          const returnTo = getReturnTo() ?? '/';
          clearReturnTo();
          const termsPath = buildPortalDestination(`/auth/terms?returnTo=${encodeURIComponent(returnTo)}`);
          window.location.replace(termsPath);
          return;
        }

        saveSessionExpiry(buildSessionScopeKey(config.clientName, config.programName), result.expiresAt);
        setStatus('success');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setErrorMessage('Your account is not authorized for this application.');
        } else {
          setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred.');
        }
        setStatus('error');
      }
    })();
  }, [config]);

  useEffect(() => {
    if (status !== 'success') return;
    const timer = window.setTimeout(() => {
      const returnTo = getReturnTo();
      clearReturnTo();
      window.location.replace(buildPortalDestination(returnTo));
    }, 150);
    return () => window.clearTimeout(timer);
  }, [status]);

  if (isLoading || (status === 'pending' && !configError)) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (configError instanceof ApiError && configError.status === 404) {
    return (
      <Container size="sm" pt="xl" className="portal-page">
        <Paper p="lg" radius="md" withBorder className="portal-panel">
          <Alert color="red" title="Not Found">
            This program could not be found.
          </Alert>
        </Paper>
      </Container>
    );
  }

  if (status === 'error') {
    return config ? (
      <PageShell
        config={config}
        headerLogoUrl={config.pages?.login?.logoUrl ?? config.theme.logoUrl}
        headerTitle={config.pages?.login?.title ?? config.theme.displayName}
        support={config.pages?.login?.showSupportInfo === true ? config.support : undefined}
      >
        <Paper p="lg" radius="md" withBorder className="portal-panel">
          <Stack gap="md">
            <Text className="portal-kicker">SSO Callback</Text>
            <Title order={4} className="portal-section-title">Unable to complete sign-in</Title>
            <Alert color="red" title="Sign-In Failed">
              {errorMessage ?? 'Unable to complete sign-in. Please try again.'}
            </Alert>
            <Button component={Link} to="/auth/request" className="portal-primary-button">
              Back to sign-in
            </Button>
          </Stack>
        </Paper>
      </PageShell>
    ) : (
      <Container size="sm" py="xl" className="portal-page">
        <Paper p="lg" radius="md" withBorder className="portal-panel">
          <Stack gap="md">
            <Text className="portal-kicker">SSO Callback</Text>
            <Title order={4} className="portal-section-title">Unable to complete sign-in</Title>
            <Alert color="red" title="Sign-In Failed">
              {errorMessage ?? 'Unable to complete sign-in. Please try again.'}
            </Alert>
            <Button component={Link} to="/auth/request" className="portal-primary-button">
              Back to sign-in
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Center h="100vh">
      <Loader size="lg" />
    </Center>
  );
}
